import { z } from 'zod';
// Companies
export const createSponsorCompanySchema = z.object({
    name: z.string().min(1).max(200),
    domain: z.string().max(255).optional(),
    industry: z.string().max(100).optional(),
    description: z.string().optional(),
    logoUrl: z.string().url().optional(),
    size: z.string().max(20).optional(),
    estimatedBudget: z.string().max(20).optional(),
});
export const updateSponsorCompanySchema = createSponsorCompanySchema.partial();
// Contacts
export const createSponsorContactSchema = z.object({
    companyId: z.string().uuid(),
    name: z.string().min(1).max(200),
    email: z.string().email().optional(),
    role: z.string().max(100).optional(),
    linkedinUrl: z.string().url().optional(),
    phone: z.string().max(30).optional(),
    isPrimary: z.boolean().optional(),
});
export const updateSponsorContactSchema = createSponsorContactSchema.partial();
// Campaigns
export const createCampaignSchema = z.object({
    name: z.string().min(1).max(200),
    goal: z.string().optional(),
    targetSponsorCount: z.number().int().positive().optional(),
    episodeId: z.string().uuid().optional(),
    startDate: z.string().datetime().optional(),
    endDate: z.string().datetime().optional(),
});
export const updateCampaignSchema = createCampaignSchema.partial().extend({
    status: z.enum(['draft', 'active', 'paused', 'completed']).optional(),
});
// Deals
export const DEAL_STAGES = [
    'discovered',
    'researching',
    'contacted',
    'replied',
    'negotiating',
    'verbal_yes',
    'signed',
    'live',
    'completed',
    'closed_won',
    'closed_lost',
];
export const createDealSchema = z.object({
    campaignId: z.string().uuid(),
    companyId: z.string().uuid(),
    contactId: z.string().uuid().optional(),
    stage: z.enum(DEAL_STAGES).optional(),
    dealValueCents: z.number().int().nonnegative().optional(),
    currency: z.string().length(3).optional(),
    dealType: z.string().max(30).optional(),
    episodeCount: z.number().int().positive().optional(),
    notes: z.string().optional(),
});
export const updateDealSchema = createDealSchema.partial();
export const moveDealStageSchema = z.object({
    stage: z.enum(DEAL_STAGES),
    closedReason: z.string().max(30).optional(),
});
// Profile + autonomy (agent onboarding). The sponsorship agent ("Hermes")
// requires a profile for the team; autonomy settings tune how far it can act.
export const upsertSponsorshipProfileSchema = z.object({
    podcastName: z.string().min(1).max(200),
    podcastDescription: z.string().optional(),
    niche: z.string().max(100).optional(),
    audienceSize: z.number().int().nonnegative().optional(),
    audienceDemographics: z
        .object({
        ageRange: z.string().optional(),
        gender: z.string().optional(),
        location: z.string().optional(),
        interests: z.array(z.string()).optional(),
    })
        .optional(),
    cpmRange: z
        .object({ min: z.number().nonnegative(), max: z.number().nonnegative() })
        .optional(),
    preferredCategories: z.array(z.string()).optional(),
    pastSponsors: z.array(z.string()).optional(),
    podcastUrls: z
        .object({
        rss: z.string().optional(),
        apple: z.string().optional(),
        spotify: z.string().optional(),
        website: z.string().optional(),
    })
        .optional(),
    onboardingCompleted: z.boolean().optional(),
    onboardingStep: z.number().int().nonnegative().optional(),
});
export const SPONSORSHIP_AUTONOMY_LEVELS = [
    'approve_each',
    'approve_first_auto_rest',
    'ai_coach_mode',
    'full_auto_with_guardrails',
];
export const upsertAutonomySettingsSchema = z.object({
    autonomyLevel: z.enum(SPONSORSHIP_AUTONOMY_LEVELS).optional(),
    maxDealValueAutoApprove: z.number().int().nonnegative().nullable().optional(),
    autoFollowUpEnabled: z.boolean().optional(),
    maxFollowUps: z.number().int().nonnegative().optional(),
    dailyOutreachLimit: z.number().int().nonnegative().optional(),
    blacklistedDomains: z.array(z.string()).optional(),
    preferredTone: z.string().max(30).optional(),
});
// Outreach templates
export const createOutreachTemplateSchema = z.object({
    name: z.string().min(1).max(200),
    category: z.string().min(1).max(30),
    subject: z.string().max(500).optional(),
    body: z.string().optional(),
});
export const updateOutreachTemplateSchema = createOutreachTemplateSchema.partial();
// Editing an outbound draft before it's sent.
export const updateOutreachMessageSchema = z
    .object({
    subject: z.string().max(500).optional(),
    body: z.string().max(20_000).optional(),
})
    .refine((v) => v.subject !== undefined || v.body !== undefined, { message: 'nothing_to_update' });
//# sourceMappingURL=sponsorship.schemas.js.map